import React, { Fragment, useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useStripe from '@afosto/utils-react/stripe/useStripe';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import PaymentMethodForm from '../../../../components/PaymentMethodForm';
import ContentDrawer from '@afosto/app-components-react/ContentDrawer';
import Icon from '@afosto/app-components-react/Icon';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import getPaymentIcon from '../../../../utils/getPaymentIcon';
import { subscription as subscriptionApi } from '@afosto/iam/api/Subscription';
import translations from './translations';

const stripeApiKey = process.env.REACT_APP_STRIPE_API_KEY;

const PaymentMethods = () => {
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:sources:read']);
  const intl = useIntl();
  const [stripeConfig] = useStripe(stripeApiKey);
  const { enqueueSnackbar } = useSnackbar();
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [isFetchingPaymentOptions, setIsFetchingPaymentOptions] = useState(true);
  const [activePaymentOptions, setActivePaymentOptions] = useState(null);
  const [loadPaymentOptions, setLoadPaymentOptions] = useState(true);

  const handleAddPaymentMethod = async source => {
    try {
      await subscriptionApi.addPaymentSource({ source });

      setShowAddDrawer(false);
      setLoadPaymentOptions(true);
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentMethodAdded), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentMethodNotAdded), {
        variant: 'error',
      });
    }
  };

  const handleDeletePaymentMethod = async id => {
    try {
      const response = await subscriptionApi.removePaymentSource(id);
      const { data = {} } = response;

      setActivePaymentOptions(data);
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentMethodDeleted), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentMethodNotDeleted), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPaymentOptions = async () => {
      setIsFetchingPaymentOptions(true);

      try {
        const response = await subscriptionApi.listPaymentSources();
        const { data = [] } = response;

        if (isMounted) {
          setActivePaymentOptions(data);
        }
      } catch (err) {
        if (isMounted) {
          setActivePaymentOptions(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPaymentOptions(false);
          setLoadPaymentOptions(false);
        }
      }
    };

    if (loadPaymentOptions) {
      fetchPaymentOptions();
    }

    return () => {
      isMounted = false;
    };
  }, [loadPaymentOptions]);

  const hasMoreThanOnePaymentMethod =
    activePaymentOptions !== null && Object.keys(activePaymentOptions).length > 1;

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            hasPermissions(['iam:sources:write']) ? (
              <Button variant="outlined" color="default" onClick={() => setShowAddDrawer(true)}>
                <FormattedMessage {...translations.create.button} />
              </Button>
            ) : null
          }
        />
        <CardContent>
          <List>
            {isFetchingPaymentOptions ? (
              <SkeletonBody lines={2} style={{ maxWidth: 150 }} />
            ) : (
              (activePaymentOptions !== null &&
                Object.keys(activePaymentOptions).length > 0 &&
                Object.keys(activePaymentOptions).map((key, index) => (
                  <Fragment key={key}>
                    <ListItem>
                      <ListItemAvatar>
                        <Icon
                          size="large"
                          color="action"
                          icon={getPaymentIcon(activePaymentOptions[key].brand)}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={activePaymentOptions[key].brand || '-'}
                        secondary={`**** **** **** ${activePaymentOptions[key].last_4 || '****'}`}
                      />
                      {hasPermissions(['iam:sources:write']) && (
                        <ListItemSecondaryAction>
                          <Tooltip
                            title={intl.formatMessage(translations.delete.disabledTooltip)}
                            disableFocusListener={hasMoreThanOnePaymentMethod}
                            disableHoverListener={hasMoreThanOnePaymentMethod}
                            disableTouchListener={hasMoreThanOnePaymentMethod}
                          >
                            <span>
                              <IconButton
                                onClick={() =>
                                  handleDeletePaymentMethod(activePaymentOptions[key].id)
                                }
                                disabled={Object.keys(activePaymentOptions).length === 1}
                              >
                                <Icon icon={['fal', 'trash-alt']} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                    {Object.keys(activePaymentOptions).length !== index + 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </Fragment>
                ))) || (
                <Typography variant="body2" align="center" color="textSecondary" gutterBottom>
                  <FormattedMessage {...translations.empty} />
                </Typography>
              )
            )}
          </List>
        </CardContent>
      </Card>
      <ContentDrawer
        title={intl.formatMessage(translations.create.title)}
        isOpen={showAddDrawer}
        handleClose={() => setShowAddDrawer(false)}
        drawerWidth="400px"
      >
        <PaymentMethodForm
          stripe={stripeConfig}
          onSubmit={handleAddPaymentMethod}
          onCancel={() => setShowAddDrawer(false)}
        />
      </ContentDrawer>
    </>
  );
};

export default PaymentMethods;
